// @flow
import { enqueueSnackbar } from "notistack";
import * as React from 'react'
import { useTranslation } from "react-i18next";
import { handleAxiosErrors } from "../../helpers/errorHandling";
import useUserData from "../../hooks/user/useUserData";
import { convertBlobToBase64, downloadFile } from "../../helpers/fileHandle";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { AXIOS_ERROR_MESSAGE } from "../../locales/components/namespaces";

type Props = {
    url: string,
    filename?: string, // now optional as fallback
    children: React.Node,
    disabled?: boolean,
}

export const AuthenticatedLink = (props: Props) => {
    const { url, filename, disabled, children, ...otherProps } = props
    const { token } = useUserData()
    const {t} = useTranslation(AXIOS_ERROR_MESSAGE)
    const headers = { 'Authorization': `Bearer ${token}` }
    
    const handleAction = async () => {
        if (disabled) return
        try {
        const result = await fetch(url, { headers })
            if(!result.ok) {
                const status = result.status
                enqueueSnackbar(t(status === 500 ? 'No data found or an error occurred!' : status === 404 ? 'No data found!' : 'An error occurred!'), {
                    variant: 'error',
                    autoHideDuration: 3000,
                })
            } else {
                const blob = await result.blob()
                
                // Extract filename from Content-Disposition header
                let disposition = result.headers.get("Content-Disposition")
                let filenameFromHeader = ""
                if (disposition && disposition.indexOf("filename=") !== -1) {
                    filenameFromHeader = disposition
                    .split("filename=")[1]
                    .split(";")[0]
                    .replace(/['"]/g, "")
                    .trim()
                }
                
                const finalFilename = filenameFromHeader || filename || "download"
                
                if (Capacitor.isNativePlatform()) {
                    const base64 = await convertBlobToBase64(blob)
                    const saveFile = await Filesystem.writeFile({
                        path: finalFilename,
                        data: base64,
                        directory: Directory.External
                    })
                    await FileOpener.open({
                        filePath: saveFile.uri,
                        contentType: blob.type
                    })
                } else {
                    const href = window.URL.createObjectURL(blob)
                    downloadFile(href, finalFilename)
                }
            }
        } catch(error) {
            enqueueSnackbar(handleAxiosErrors(error, t('No data found or an error occurred!'), {
                variant: 'error',
                autoHideDuration: 3000,
            }))
        }
       
    }
    
    return (
        <a
            onClick={handleAction}
            {...otherProps}
        >
            {children}
        </a>
    )
}
